import { z } from "zod";

/**
 * Shared DTO cho Admin Security Threat board (US-AE9.F3-0030 · @UC9082 security-threat-board).
 * Một nguồn sự thật cho api (S01/S03) và admin web (S04). Board TỔNG HỢP tín hiệu bảo mật THẬT
 * trong repo — KHÔNG bịa nguồn không tồn tại (Decision Record PROGRESS.md):
 *
 *  - `bruteForce`  : DERIVE live từ AdminLoginAttempt (đếm fail theo email/ip trong window).
 *  - `tokenReuse`  : GHI vào security_threat_events khi admin refresh-token reuse-detection trigger.
 *  - `rateLimit` / `waf` / `anomaly` : KHÔNG có engine/log persistence trong repo (throttler in-memory,
 *    WAF là dịch vụ ngoài) → KHÔNG khả dụng từ live data (không bịa số). Chỉ 2 nguồn live ở trên.
 *
 * BẤT BIẾN (invariants):
 * - C2 no-PII thô: item mang ref đã mask (source = IP/CIDR/email-đã-mask), KHÔNG email/tên thô.
 * - C1 no-SMS: alert token-reuse chỉ teams/email (contracts alert đã enforce ở admin-observability).
 * - `.strict()` cho body ghi (triage patch) — chống over-post/field lạ.
 * - từ ≤ đến (window range) — validate tại biên.
 */

// ============================================================
// Enum-lite (khớp badge STATUS admin + mock resources.ts threats)
// ============================================================

/** Mức nghiêm trọng đe doạ (khớp mock resources.ts kind: high/medium/low). */
export const threatSeveritySchema = z.enum(["high", "medium", "low"]);
export type ThreatSeverity = z.infer<typeof threatSeveritySchema>;

/**
 * Loại đe doạ — chỉ nguồn THẬT trong repo. `brute_force` (derive AdminLoginAttempt) +
 * `token_reuse` (event table). KHÔNG có waf/sqli/anomaly (không engine — không bịa).
 */
export const threatKindSchema = z.enum(["brute_force", "token_reuse"]);
export type ThreatKind = z.infer<typeof threatKindSchema>;

/** Hành động đã áp cho đe doạ (khớp mock action: blocked). */
export const threatActionSchema = z.enum(["blocked", "monitored"]);
export type ThreatAction = z.infer<typeof threatActionSchema>;

/**
 * Trạng thái triage (khớp mock status: resolved/investigating; open = mới chưa xử lý).
 * Chỉ threat-event rows (token_reuse) triage được; brute-force derive luôn 'open' (không id bền).
 */
export const threatStatusSchema = z.enum(["open", "investigating", "resolved"]);
export type ThreatStatus = z.infer<typeof threatStatusSchema>;

// ============================================================
// Item + Summary (READ)
// ============================================================

/**
 * Một dòng đe doạ trên board. `id` bền vững CHỈ với token_reuse (event table) — brute-force derive
 * dùng id tổng hợp `bf:<hash>` KHÔNG triage được (chỉ hiển thị + khoá account qua suspend).
 * `source` = IP/CIDR hoặc email đã mask (C2 — KHÔNG PII thô). `detail` mô tả redacted.
 */
export const threatItemSchema = z
  .object({
    id: z.string(),
    kind: threatKindSchema,
    severity: threatSeveritySchema,
    /** ISO 8601 thời điểm sự kiện (mới nhất với brute-force cụm). */
    occurredAt: z.string(),
    /** Nguồn đã mask: CIDR/IP/email-mask/"admin-session" — KHÔNG PII thô (C2). */
    source: z.string(),
    /** Mô tả redacted (vd "Brute-force login · 240 lần/5′"). */
    detail: z.string(),
    action: threatActionSchema,
    status: threatStatusSchema,
    /**
     * userId liên quan (nếu truy được) để hành động khoá account (suspend). null nếu không map
     * được user (vd brute-force theo email lạ chưa từng đăng ký). KHÔNG PII — chỉ id.
     */
    subjectUserId: z.string().nullable(),
    /** true ⇒ dòng có id bền (event) → triage được; false ⇒ derive (chỉ hiển thị). */
    triageable: z.boolean(),
  })
  .strict();

export type ThreatItem = z.infer<typeof threatItemSchema>;

/**
 * Summary đếm theo loại trong window. `available: false` cho nguồn KHÔNG khả dụng (rate-limit/waf/
 * anomaly) → UI hiển thị "không khả dụng" thay vì số 0 giả (AC5 — không bịa).
 */
export const threatSummarySchema = z
  .object({
    /** Khung thời gian đã áp (echo lại từ query; ISO). */
    from: z.string(),
    to: z.string(),
    /** Đếm nguồn LIVE. */
    bruteForce: z.number().int().min(0),
    tokenReuse: z.number().int().min(0),
    /** Nguồn KHÔNG khả dụng trong repo — đánh dấu rõ (không số giả). */
    unavailable: z.array(z.enum(["rate_limit", "waf", "anomaly"])),
  })
  .strict();

export type ThreatSummary = z.infer<typeof threatSummarySchema>;

/** Envelope board: summary + items (cursor pagination) `{ summary, data, pageInfo }`. */
export const threatBoardResponseSchema = z
  .object({
    summary: threatSummarySchema,
    data: z.array(threatItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type ThreatBoardResponse = z.infer<typeof threatBoardResponseSchema>;

// ============================================================
// Query (GET /admin/security/threats)
// ============================================================

export const THREAT_BOARD_MAX_LIMIT = 100;
/** Cửa sổ mặc định khi không truyền from/to: 7 ngày gần nhất (ms). */
export const THREAT_BOARD_DEFAULT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Query board — window (from/to ISO, from ≤ to), filter severity/kind/status, cursor + limit.
 * from/to optional (mặc định 7 ngày). `.strict()` chặn param lạ. refine from ≤ to.
 */
export const threatBoardQuerySchema = z
  .object({
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    severity: threatSeveritySchema.optional(),
    kind: threatKindSchema.optional(),
    status: threatStatusSchema.optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(THREAT_BOARD_MAX_LIMIT).optional(),
  })
  .strict()
  .refine(
    (v) => !v.from || !v.to || new Date(v.from).getTime() <= new Date(v.to).getTime(),
    { message: "Khung thời gian không hợp lệ: from phải ≤ to", path: ["from"] },
  );

export type ThreatBoardQuery = z.infer<typeof threatBoardQuerySchema>;

// ============================================================
// Triage (PATCH /admin/security/threats/:id) — chỉ token_reuse event rows
// ============================================================

/**
 * Body triage 1 threat-event (mark investigating/resolved). `.strict()`. status bắt buộc; reason
 * optional (audit). CHỈ áp cho token_reuse event rows (id bền). Brute-force derive KHÔNG triage.
 */
export const THREAT_TRIAGE_REASON_MAX_LENGTH = 500;
export const threatTriageBodySchema = z
  .object({
    /** open không cho set qua triage (chỉ tiến investigating/resolved). */
    status: z.enum(["investigating", "resolved"]),
    reason: z.string().max(THREAT_TRIAGE_REASON_MAX_LENGTH).optional(),
  })
  .strict();

export type ThreatTriageBody = z.infer<typeof threatTriageBodySchema>;
